/**
 * Coinbase API-key helpers for the Vault screen (ADR-0022).
 *
 * Pure functions, deliberately: `.svelte` components stay thin and the logic here is unit-tested
 * (docs/engineering/TDD.md). The authoritative key-type decision is the SERVER's — this only
 * mirrors it well enough to guide someone mid-paste.
 */

// Venue ids live in lib/venues.js (the one registry — ADR-0040); this module is key-format only.

export const KEY_TYPE = {
  ED25519: 'ed25519',
  ECDSA: 'ecdsa',
  UNKNOWN: 'unknown',
  EMPTY: 'empty',
};

/**
 * Guesses which Coinbase key type a pasted secret is, mirroring the server's detection cascade:
 * PEM armor first, then raw base64 by decoded length.
 *
 * A guess only — the server re-derives this authoritatively and verifies the key against Coinbase
 * before storing it. This exists so the form can say "Ed25519 detected" while you paste.
 *
 * @param {string} secret Raw contents of the API-secret field.
 * @returns {string} One of {@link KEY_TYPE}.
 */
export function detectKeyType(secret) {
  if (!secret || !secret.trim()) return KEY_TYPE.EMPTY;

  const text = secret.trim();

  if (text.includes('-----BEGIN EC PRIVATE KEY-----')) return KEY_TYPE.ECDSA;

  // PKCS#8 armor is shared by both algorithms; only the DER OID separates them, and decoding DER
  // in the browser to guess a label is not worth it. The server decides.
  if (text.includes('-----BEGIN PRIVATE KEY-----')) return KEY_TYPE.UNKNOWN;

  // Any other PEM is an algorithm Coinbase does not issue (RSA, OpenSSH, ...).
  if (text.includes('-----BEGIN')) return KEY_TYPE.UNKNOWN;

  const compact = text.replace(/\s+/g, '');
  if (!/^[A-Za-z0-9+/]+={0,2}$/.test(compact)) return KEY_TYPE.UNKNOWN;

  // Ed25519 secrets decode to exactly 64 bytes (seed ‖ public key) or 32 (bare seed). base64 is
  // 4 chars per 3 bytes, so 64 bytes -> 88 chars with padding, 32 -> 44.
  const bytes = decodedByteLength(compact);
  if (bytes === 64 || bytes === 32) return KEY_TYPE.ED25519;

  // A SEC1 P-256 key decodes to ~118-122 bytes. Only that band earns the reassuring "legacy key,
  // still works" label: the server hands anything else to ImportECPrivateKey, which rejects
  // non-SEC1 DER — so calling a ~100-byte truncated paste "ECDSA" would promise a save that then
  // 400s. Outside the band we defer rather than reassure.
  if (bytes >= 110 && bytes <= 130) return KEY_TYPE.ECDSA;

  return KEY_TYPE.UNKNOWN;
}

/** Byte length a base64 string decodes to, without actually decoding it. */
function decodedByteLength(base64) {
  if (base64.length % 4 !== 0) return -1;
  const padding = (base64.match(/=*$/) || [''])[0].length;
  return (base64.length / 4) * 3 - padding;
}

/**
 * Human-facing guidance for a detected key type.
 *
 * @param {string} keyType A {@link KEY_TYPE} value.
 * @returns {{label: string, tone: string, hint: string}|null} Null when there is nothing to say yet.
 */
export function describeKeyType(keyType) {
  switch (keyType) {
    case KEY_TYPE.ED25519:
      return {
        label: 'Ed25519 key detected',
        tone: 'success',
        hint: 'This is the current Coinbase key format.',
      };
    case KEY_TYPE.ECDSA:
      return {
        label: 'ECDSA key detected',
        tone: 'info',
        hint: 'This is the legacy format. It still works — new Coinbase keys are Ed25519.',
      };
    case KEY_TYPE.UNKNOWN:
      return {
        label: 'Unrecognized key format',
        tone: 'warning',
        hint: 'Expected an Ed25519 secret, or a legacy ECDSA key beginning "-----BEGIN EC PRIVATE KEY-----". It will be verified with Coinbase when you save.',
      };
    default:
      return null;
  }
}

/**
 * Pulls the server's explanation out of a failed save.
 *
 * The shared fetch wrapper throws `Error("HTTP error! status: 400, details: {json}")`, so the
 * useful message is embedded in a string rather than available as an object. Without this the user
 * sees a raw HTTP dump — or, as before, a generic "please try again" that hides the real cause.
 *
 * @param {unknown} error The thrown error.
 * @param {string} fallback Message to show when nothing better can be extracted.
 * @returns {string} A message fit to display.
 */
export function extractSaveError(error, fallback = 'Failed to save credentials. Please try again.') {
  const raw = error?.message;
  if (typeof raw !== 'string') return fallback;

  const start = raw.indexOf('{');
  if (start !== -1) {
    try {
      const body = JSON.parse(raw.slice(start));
      // The controller returns { error, details }; details carries the actionable half.
      const detail = body?.details || body?.error;
      if (detail) return detail;
    } catch {
      // Not JSON after all — fall through.
    }
  }

  return fallback;
}
